import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createCheckout } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, total, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    const payload = items
      .filter((i) => i.variant_id)
      .map((i) => ({ variant_id: i.variant_id, quantity: i.quantity }));
    if (!payload.length) {
      toast.error("These items are unavailable for checkout.");
      return;
    }
    setLoading(true);
    setRedirecting(true);
    try {
      const { checkout_url } = await createCheckout(payload);
      // Let the overlay paint/animate before handing off to Shopify
      setTimeout(() => {
        window.location.href = checkout_url;
      }, 650);
    } catch {
      setRedirecting(false);
      setLoading(false);
      toast.error("Checkout failed. Please try again.");
    }
  };

  return (
    <>
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70]"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#080808] border-l border-white/10 z-[80] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-white/10">
              <h3 className="font-display text-xl font-bold tracking-tight text-white">
                Cart <span className="text-steel-light">/ {count}</span>
              </h3>
              <button
                data-testid="close-cart-btn"
                onClick={() => setOpen(false)}
                className="text-steel-light hover:text-white transition-colors"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <p className="font-mono-tech text-xs uppercase tracking-widest text-steel">
                    The line is empty
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((i) => (
                    <div
                      key={i.key}
                      data-testid={`cart-item-${i.lookup_key}`}
                      className="flex gap-4"
                    >
                      <img
                        src={i.image}
                        alt={i.name}
                        className="w-20 h-24 object-cover border border-white/10 saturate-[0.5]"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <h4 className="font-display font-bold text-white">{i.name}</h4>
                          <span className="font-mono-tech text-sm text-white">
                            ${(i.price * i.quantity).toFixed(0)}
                          </span>
                        </div>
                        <p className="font-mono-tech text-[10px] uppercase tracking-wide text-steel-light mt-1">
                          {i.size} · {i.color}
                        </p>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            data-testid={`qty-dec-${i.lookup_key}`}
                            onClick={() => updateQty(i.key, -1)}
                            className="w-6 h-6 border border-white/25 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="font-mono-tech text-sm text-white w-4 text-center">
                            {i.quantity}
                          </span>
                          <button
                            data-testid={`qty-inc-${i.lookup_key}`}
                            onClick={() => updateQty(i.key, 1)}
                            className="w-6 h-6 border border-white/25 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => removeItem(i.key)}
                            className="ml-auto font-mono-tech text-[10px] uppercase tracking-widest text-steel hover:text-white transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-6 border-t border-white/10">
              <div className="flex justify-between mb-1">
                <span className="font-mono-tech text-xs uppercase tracking-widest text-steel-light">
                  Subtotal
                </span>
                <span className="font-mono-tech text-lg text-white">
                  ${total.toFixed(2)}
                </span>
              </div>
              <p className="font-mono-tech text-[10px] text-steel mb-5">
                Taxes calculated at checkout.
              </p>
              <button
                data-testid="checkout-btn"
                onClick={checkout}
                disabled={!items.length || loading}
                className="group relative w-full overflow-hidden border border-white bg-white text-black py-4 disabled:opacity-40"
              >
                <span className="relative z-10 font-mono-tech text-xs uppercase tracking-widest flex items-center justify-center gap-2 group-hover:text-white transition-colors duration-300">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Redirecting
                    </>
                  ) : (
                    "Checkout"
                  )}
                </span>
                <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {redirecting && (
        <motion.div
          data-testid="checkout-redirect-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[120] bg-[#050505] flex flex-col items-center justify-center px-6"
        >
          <div className="grain absolute inset-0 pointer-events-none opacity-60" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative text-center"
          >
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-steel-light mb-6">
              Secure checkout
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-white mb-10">
              Boarding the line<span className="animate-pulse">…</span>
            </h2>

            {/* Animated train line */}
            <div className="relative w-64 md:w-80 h-[2px] bg-white/15 mx-auto overflow-hidden">
              <motion.div
                className="absolute top-0 left-0 h-full flex items-center gap-1"
                initial={{ x: "-30%" }}
                animate={{ x: "130%" }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              >
                <span className="block w-14 h-[2px] bg-white" />
                <span className="block w-6 h-[2px] bg-white/60" />
                <span className="block w-2 h-[2px] bg-white/30" />
              </motion.div>
            </div>
            <p className="font-mono-tech text-[11px] text-steel mt-8">
              Redirecting to secure checkout
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
